import React, { useContext, useState } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  setDoc,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { AuthContext } from "../context/AuthContext";
import { v4 as uuid } from "uuid";
import toast from "react-hot-toast";

const CreateGroup = ({ onClose }) => {
  const [groupName, setGroupName] = useState("");
  const [username, setUsername] = useState("");
  const [user, setUser] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [groupLoading, setGroupLoading] = useState(false);

  const { currentUser, userEventCode, loading: authLoading } = useContext(AuthContext);

  const handleSearch = async () => {
    if (!username.trim()) {
      toast.error("Please enter a username to search");
      return;
    }
    
    // Get eventCode with fallback
    const eventCode = userEventCode || currentUser?.eventCode;
    
    if (!eventCode) {
      toast.error("Event code not found. Please refresh the page.");
      return;
    }
    
    const q = query(
      collection(db, "users"),
      where("displayName", "==", username)
    );

    try {
      const querySnapshot = await getDocs(q);
      let foundUser = null;
      querySnapshot.forEach((doc) => {
        const userData = doc.data();
        // Check if user is in the same event
        const userEvents = userData.events || [userData.eventCode].filter(Boolean);
        if (userEvents.includes(eventCode)) {
          foundUser = userData;
        }
      });
      
      if (foundUser && foundUser.uid !== currentUser.uid) {
        // Check if user is already selected
        if (selectedUsers.find(u => u.uid === foundUser.uid)) {
          toast.error("User already added to the group");
        } else {
          setUser(foundUser);
          toast.success(`Found user: ${foundUser.displayName}`);
        }
      } else if (foundUser && foundUser.uid === currentUser.uid) {
        setUser(null);
        toast.error("You cannot add yourself to the group");
      } else {
        setUser(null);
        toast.error("User not found in this event");
      }
    } catch (err) {
      console.error("Search error:", err);
      toast.error("Error searching for user. Please try again.");
    }
  };

  const handleKey = (e) => {
    e.code === "Enter" && handleSearch();
  };

  const handleAddUser = () => {
    if (user && !selectedUsers.find(u => u.uid === user.uid)) {
      setSelectedUsers([...selectedUsers, user]);
      setUser(null);
      setUsername("");
      toast.success(`${user.displayName} added to group`);
    }
  };

  const handleRemoveUser = (userId) => {
    const removedUser = selectedUsers.find(u => u.uid === userId);
    setSelectedUsers(selectedUsers.filter(u => u.uid !== userId));
    if (removedUser) {
      toast.success(`${removedUser.displayName} removed from group`);
    }
  };

  const handleCreateGroup = async () => {
    if (!groupName.trim()) {
      toast.error("Please enter a group name");
      return;
    }
    
    if (selectedUsers.length === 0) {
      toast.error("Please add at least one user to the group");
      return;
    }

    setGroupLoading(true);
    try {
      toast.loading("Creating group...", { id: "createGroup" });
      
      const groupId = uuid();
      const allMembers = [currentUser, ...selectedUsers];
      
      // Get eventCode with fallback
      const eventCode = userEventCode || currentUser?.eventCode;
      
      // Create group chat document
      await setDoc(doc(db, "groupChats", groupId), {
        messages: [],
        groupInfo: {
          name: groupName,
          createdBy: currentUser.uid,
          createdAt: serverTimestamp(),
          eventCode: eventCode,
          members: allMembers.map(u => ({
            uid: u.uid,
            displayName: u.displayName,
            photoURL: u.photoURL,
          })),
        },
      });

      // Update each member's userChats
      for (const member of allMembers) {
        const memberChatId = `${member.uid}_${eventCode}`;
        
        // Try event-specific userChats first
        try {
          await updateDoc(doc(db, "userChats", memberChatId), {
            [groupId]: {
              isGroup: true,
              eventCode: eventCode,
              groupInfo: {
                name: groupName,
                members: allMembers.map(u => ({
                  uid: u.uid,
                  displayName: u.displayName,
                  photoURL: u.photoURL,
                })),
              },
              lastMessage: {
                text: `${currentUser.displayName} created the group`,
              },
              date: serverTimestamp(),
            },
          });
        } catch (error) {
          // Fallback to old format if event-specific doesn't exist
          await updateDoc(doc(db, "userChats", member.uid), {
            [groupId]: {
              isGroup: true,
              eventCode: eventCode,
              groupInfo: {
                name: groupName,
                members: allMembers.map(u => ({
                  uid: u.uid,
                  displayName: u.displayName,
                  photoURL: u.photoURL,
                })),
              },
              lastMessage: {
                text: `${currentUser.displayName} created the group`,
              },
              date: serverTimestamp(),
            },
          });
        }
      }

      toast.success(`Group "${groupName}" created successfully!`, { id: "createGroup" });
      onClose();
    } catch (error) {
      console.error("Error creating group:", error);
      toast.error("Failed to create group. Please try again.", { id: "createGroup" });
    }
    setGroupLoading(false);
  };

  return (
    <div className="createGroup">
      <div className="createGroupModal">
        <div className="createGroupHeader">
          <h3>Create Group Chat</h3>
          <button onClick={onClose} className="closeBtn">×</button>
        </div>
        
        <div className="createGroupContent">
          <input
            type="text"
            placeholder="Group name"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            className="groupNameInput"
          />

          <div className="searchForm">
            <input
              type="text"
              placeholder="Add users by username"
              onKeyDown={handleKey}
              onChange={(e) => setUsername(e.target.value)}
              value={username}
            />
            <button onClick={handleSearch}>Search</button>
          </div>

          {user && (
            <div className="userChat" onClick={handleAddUser}>
              <img src={user.photoURL} alt="" />
              <div className="userChatInfo">
                <span>{user.displayName}</span>
              </div>
              <button>Add</button>
            </div>
          )}

          {selectedUsers.length > 0 && (
            <div className="selectedUsers">
              <h4>Selected Users:</h4>
              {selectedUsers.map((user) => (
                <div key={user.uid} className="selectedUser">
                  <img src={user.photoURL} alt="" />
                  <span>{user.displayName}</span>
                  <button onClick={() => handleRemoveUser(user.uid)}>Remove</button>
                </div>
              ))}
            </div>
          )}

          <button 
            onClick={handleCreateGroup}
            disabled={groupLoading || !groupName.trim() || selectedUsers.length === 0}
            className="createBtn"
          >
            {groupLoading ? "Creating..." : "Create Group"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateGroup;
